import { Controller } from "@hotwired/stimulus"

/**
 * Admin Form Dialog Controller - Dialog with form for user input
 *
 * Features:
 * - Form submission handling
 * - Escape key to close
 * - Body scroll lock when open
 * - Backdrop click to close
 * - Smooth transitions
 * - Form validation
 *
 * Usage:
 * <button data-action="click->admin-form-dialog#open">
 *   Ban User
 * </button>
 */
export default class extends Controller {
  static targets = ["container", "backdrop", "dialog"]

  connect() {
    // Listen for escape key
    this.boundHandleKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.close()
  }

  // Open dialog from trigger button
  open(event) {
    event?.preventDefault()

    // Show container
    this.containerTarget.classList.remove("hidden")

    // Lock body scroll
    document.body.style.overflow = "hidden"

    // Add keydown listener
    document.addEventListener("keydown", this.boundHandleKeydown)

    // Animate in
    requestAnimationFrame(() => {
      this.backdropTarget.classList.add("opacity-100")
      this.dialogTarget.classList.add("scale-100", "opacity-100")

      // Focus first input
      const firstInput = this.dialogTarget.querySelector("input, textarea, select")
      if (firstInput) {
        firstInput.focus()
      }
    })
  }

  // Close dialog
  close(event) {
    event?.preventDefault()

    // Animate out
    this.backdropTarget.classList.remove("opacity-100")
    this.dialogTarget.classList.remove("scale-100", "opacity-100")

    // Hide after animation
    setTimeout(() => {
      this.containerTarget.classList.add("hidden")

      // Unlock body scroll
      document.body.style.overflow = ""

      // Remove keydown listener
      document.removeEventListener("keydown", this.boundHandleKeydown)

      // Reset form
      const form = this.dialogTarget.querySelector("form")
      if (form) {
        form.reset()
      }
    }, 150)
  }

  // Handle escape key
  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  // Handle form submission
  submit(event) {
    const form = event.target

    // Check if form is valid
    if (!form.checkValidity()) {
      return
    }

    // Close dialog after submission
    // Form will be submitted by Rails
    setTimeout(() => {
      this.close()
    }, 100)
  }
}
